import React, { useLayoutEffect } from 'react';
import { Box, Typography } from '@mui/material';
import QuoteSelectorComponent from '../QuoteSelector/QuoteSelectorComponent';
import { useNavigate } from 'react-router-dom';
import {
  LAUNCH_PAD_CATEGORY_ORDER,
  LAUNCH_PAD_PRODUCT_OPTIONS,
  getBaseProductRoute,
} from '../../../utils/productNavigation';
import { toSentenceCase, toTitleCase } from '../../../utils/casing';
import ScrollFadeWrapper from '../../ScrollFadeWrapper/ScrollFadeWrapper';
import Image from '../../Image/Image';
import {
  CATEGORY_ICON_NAMES,
  CATEGORY_LABELS,
  HOME_STYLE_LAUNCH_PADS,
  HOME_STYLE_PATHS,
  ICON_BASE_URL,
  LAUNCHPAD_BASE_URL,
  LAUNCH_PAD_PRODUCT_ROUTES,
  LAUNCH_PAD_PRODUCT_STEP_ROUTES,
  launchPadImageConfig,
} from './launchPadContent';

const ImageIcon = ({ name, size = 24 }) => (
  <img
    src={`${ICON_BASE_URL}${encodeURIComponent(name)}.svg`}
    alt={`${name} icon`}
    width={size}
    height={size}
    style={{ display: 'block' }}
  />
);

const ICONS = {
  Motor: <ImageIcon name={CATEGORY_ICON_NAMES.Motor} />,
  Health: <ImageIcon name={CATEGORY_ICON_NAMES.Health} />,
  Life: <ImageIcon name={CATEGORY_ICON_NAMES.Life} />,
  Money: <ImageIcon name={CATEGORY_ICON_NAMES.Money} />,
  Business: <ImageIcon name={CATEGORY_ICON_NAMES.Business} />,
  Internet: <ImageIcon name={CATEGORY_ICON_NAMES.Internet} />,
};

// The home-style layout (home and category pages) is mirrored in
// src/staticHero/staticHero.css (docs/static-hero.md): change both, or the
// page jumps when React takes over.
const LaunchPad = ({ pathName, hideDropdown = false, onImageLoad = null }) => {
  const navigate = useNavigate();
  const resolvedPathName = getBaseProductRoute(pathName);
  const isHomeStyle = HOME_STYLE_PATHS.includes(resolvedPathName);

  const productOptions = LAUNCH_PAD_PRODUCT_OPTIONS;

  const determineLine = () => {
    const homeStyleLaunchPad = HOME_STYLE_LAUNCH_PADS[resolvedPathName];
    if (homeStyleLaunchPad) return homeStyleLaunchPad;

    switch (resolvedPathName) {
      case '/car-insurance-quote':
        return {
          header: 'Compare 12 Car Insurance Quotes',
          byLine: 'Save R539 per month.',
          button: 'Get started',
          selectedOption: 'Car Insurance',
          activeIcon: 'Motor',
          hideDropdown: true,
        };
      case '/pet-insurance-quote':
        return {
          header: 'Find The Best Pet Insurance ',
          byLine:
            'Compare Pet Medical Aid plans from trusted providers and keep those tails wagging.',
          button: 'Get started',
          selectedOption: 'Pet Insurance',
          activeIcon: 'Life',
          hideDropdown: true,
        };
      case '/funeral-insurance-quote':
        return {
          header: 'Find the Best Funeral Insurance in Minutes',
          byLine:
            'Compare trusted Funeral cover quotes from leading South African providers. Hippo makes it simple, secure, and free to compare.',
          button: 'Get started',
          selectedOption: 'Funeral Insurance',
          activeIcon: 'Life',
          hideDropdown: true,
        };
      case '/life-insurance-quote':
        return {
          header: 'Find Better Life Insurance',
          byLine: 'Find the right life cover in just a few minutes ',
          button: 'Get started',
          selectedOption: 'Life Insurance',
          activeIcon: 'Life',
          hideDropdown: true,
        };
      case '/medical-aid-quote':
        return {
          header: 'Find the best medical aid',
          byLine:
            'Compare affordable medical aid plans and get quotes in under 2 minutes',
          button: 'Get started',
          selectedOption: 'Medical Aid',
          activeIcon: 'Health',
          hideDropdown: true,
        };
      case '/medical-gap-cover-quote':
        return {
          header: 'Find the Best Medical Gap Cover',
          byLine:
            'Compare medical gap cover insurance and make sure you’re never out of pocket after hospital visits.',
          button: 'Get started',
          selectedOption: 'Gap Cover',
          activeIcon: 'Health',
          hideDropdown: true,
        };
      case '/medical-insurance-quotes':
        return {
          header: 'Compare Medical Insurance Quotes',
          byLine: 'Find health cover that fits your needs and budget',
          button: 'Get started',
          selectedOption: 'Medical Insurance',
          activeIcon: 'Health',
          hideDropdown: true,
        };
      case '/travel-insurance':
        return {
          header: 'Find Better Travel Insurance ',
          byLine:
            'Compare quotes from trusted providers and enjoy peace of mind on every trip.',
          button: 'Get started',
          selectedOption: 'Travel Insurance',
          activeIcon: 'Life',
          hideDropdown: true,
        };
      case '/motor-warranty':
        return {
          header: 'Find The Best Motor Warranty Plan',
          byLine:
            'Compare warranty quotes from trusted providers and keep your car covered for less.',
          button: 'Get started',
          selectedOption: 'Motor Warranty',
          activeIcon: 'Motor',
          hideDropdown: true,
        };
      // Money
      case '/personal-loans':
        return {
          header: 'Compare Personal Loans in South Africa',
          byLine:
            'Compare loan amounts, repayment terms, and interest rates from trusted and vetted South African providers in minutes.',
          button: 'Get started',
          selectedOption: 'Personal Loans',
          activeIcon: 'Money',
          hideDropdown: true,
        };
      case '/debt-counselling':
        return {
          header: 'Debt Counselling in South Africa',
          byLine:
            'Understand debt review and compare trusted debt counselling options',
          button: 'Get started',
          selectedOption: 'Debt Counselling',
          activeIcon: 'Money',
          hideDropdown: true,
        };
      case '/debt-consolidation-loans':
        return {
          header: 'Debt Consolidation Loans',
          byLine:
            'Compare debt consolidation options from trusted South African providers',
          button: 'Get started',
          selectedOption: 'Debt Consolidation',
          activeIcon: 'Money',
          hideDropdown: true,
        };
      // Motor extras
      case '/dents-and-scratches-quote':
        return {
          header: 'Compare Scratch & Dent Cover Quotes',
          byLine:
            'Get minor damage cover that helps with unexpected dents and scratches',
          button: 'Get started',
          selectedOption: 'Scratch & Dent Cover',
          activeIcon: 'Motor',
          hideDropdown: true,
        };
      case '/tyre-and-rim-guard':
        return {
          header: 'Find Tyre and Rim Insurance',
          byLine: 'Get the right cover in just a few minutes!',
          button: 'Get started',
          selectedOption: 'Tyre & Rim Cover',
          activeIcon: 'Motor',
          hideDropdown: true,
        };
      case '/vehicle-tracking-quote':
        return {
          header: 'Find the Right Vehicle Tracker',
          byLine:
            'Compare car tracker quotes in mins. Find the right protection for you vehicle.',
          button: 'Get started',
          selectedOption: 'Vehicle Tracking',
          activeIcon: 'Motor',
          hideDropdown: true,
        };
      case '/service-plan-quotes':
        return {
          header: 'Find the Right Service Plan',
          byLine:
            'Compare quotes on service or motor plans in just a few minutes!',
          button: 'Get started',
          selectedOption: 'Service Plans',
          activeIcon: 'Motor',
          hideDropdown: true,
        };
      // Life
      case '/home-insurance-quote':
        return {
          header: 'Compare Home Insurance Quotes',
          byLine:
            'Protect your home with cover that fits your budget in minutes',
          button: 'Get started',
          selectedOption: 'Home Contents Cover',
          activeIcon: 'Life',
          hideDropdown: true,
        };
      case '/buildings-insurance-quote':
        return {
          header: 'Find Better\nBuildings Insurance',
          byLine:
            'Compare quotes from trusted insurers and protect your home from costly repairs and damages.',
          button: 'Get started',
          selectedOption: 'Building Insurance',
          activeIcon: 'Life',
          hideDropdown: true,
        };
      // Business
      case '/business-insurance-quote':
        return {
          header: 'Compare Business Insurance Quotes',
          byLine: 'Find the right cover for your business in minutes',
          button: 'Get started',
          selectedOption: 'Business Insurance',
          activeIcon: 'Business',
          hideDropdown: true,
        };
      case '/business-insurance-quote/business-legal-cover':
        return {
          header: 'Compare Business Legal Cover',
          byLine:
            'Get legal protection that fits your business needs in minutes',
          button: 'Get started',
          selectedOption: 'Business Legal Cover',
          activeIcon: 'Business',
          hideDropdown: true,
        };
      case '/business-insurance-quote/professional-liability-insurance-cover':
        return {
          header: 'Compare Professional Indemnity Quotes',
          byLine: 'Protect your business against professional liability claims',
          button: 'Get started',
          selectedOption: 'Professional Liability Insurance',
          activeIcon: 'Business',
          hideDropdown: true,
        };
      // Internet
      case '/compare-fibre-deals':
        return {
          header: 'Compare Fibre deals in minutes',
          byLine:
            'Find fast, reliable Fibre packages from leading providers and get the best speed and price for your address',
          button: 'Get started',
          selectedOption: 'Fibre Internet',
          activeIcon: 'Internet',
          hideDropdown: true,
        };
      // Motorcycle
      case '/motorcycle-insurance-quote':
        return {
          header: 'Compare Motorcycle Insurance Quotes',
          byLine: 'Get bike cover that fits your needs and budget in minutes',
          button: 'Get started',
          selectedOption: 'Motorbike Insurance',
          activeIcon: 'Motor',
          hideDropdown: true,
        };
      case '/employee-benefits-quote':
        return {
          header: 'Medical Aid for Your Employees',
          byLine: 'Compare corporate medical aid options for your team',
          button: 'Get started',
          selectedOption: 'Employee Benefits',
          activeIcon: 'Business',
          hideDropdown: true,
        };

      case '/business-insurance-quote/car-and-commercial-property-insurance':
        return {
          header: 'Compare Car & Commercial Property Insurance',
          byLine:
            'Get cover for your vehicles and business property in minutes',
          button: 'Get started',
          selectedOption: 'Commercial Property Insurance',
          activeIcon: 'Business',
          hideDropdown: true,
        };
      // Bundle
      case '/online-insurance-quote':
        return {
          header: 'Find better car & home bundles',
          byLine: 'Compare quotes in 2 minutes',
          button: 'Get started',
          selectedOption: 'Combo Insurance',
          activeIcon: 'Motor',
          hideDropdown: true,
        };
      // Default / unknown routes
      default: {
        const lower = (resolvedPathName || '').toLowerCase();

        let inferredIcon = 'Motor';

        if (lower.includes('business')) {
          inferredIcon = 'Business';
        } else if (
          lower.includes('medical') ||
          lower.includes('health') ||
          lower.includes('gap')
        ) {
          inferredIcon = 'Health';
        } else if (
          lower.includes('life') ||
          lower.includes('funeral') ||
          lower.includes('travel') ||
          lower.includes('pet') ||
          lower.includes('home') ||
          lower.includes('building')
        ) {
          inferredIcon = 'Life';
        } else if (lower.includes('loan') || lower.includes('debt')) {
          inferredIcon = 'Money';
        } else if (lower.includes('fibre') || lower.includes('internet')) {
          inferredIcon = 'Internet';
        }

        return {
          header: 'Compare Quotes\nin 2 min',
          byLine:
            'Find better Insurance, Medical Aid, Loans, and more, in just 2 min.',
          button: 'Get quotes',
          selectedOption: '',
          activeIcon: inferredIcon,
        };
      }
    }
  };

  const determineImage = () => {
    const cfg =
      launchPadImageConfig[resolvedPathName] || launchPadImageConfig.default;

    const handleImageLoad = () => {
      if (typeof onImageLoad === 'function') {
        onImageLoad();
      }
    };

    return (
      <Image
        src={`${LAUNCHPAD_BASE_URL}${encodeURIComponent(cfg.file)}`}
        alt=""
        width={cfg.width}
        height={cfg.height}
        loading="eager"
        fetchPriority="high"
        onLoad={handleImageLoad}
        onError={handleImageLoad}
        style={{
          width: `${cfg.width}px`,
          height: `${cfg.height}px`,
          display: 'block',
        }}
      />
    );
  };

  const {
    header,
    byLine,
    button,
    selectedOption: initialSelectedOption,
    activeIcon: initialActiveIcon,
    hideDropdown: routeHideDropdown = false,
  } = determineLine();

  const effectiveHideDropdown = hideDropdown || routeHideDropdown;

  const [activeIcon, setActiveIcon] = React.useState(initialActiveIcon);
  const [selectedProduct, setSelectedProduct] = React.useState(
    initialSelectedOption
  );
  const headerRef = React.useRef(null);
  const [bottomSpacer, setBottomSpacer] = React.useState(0);

  React.useEffect(() => {
    const { selectedOption, activeIcon: newIcon } = determineLine();
    setActiveIcon(newIcon);
    setSelectedProduct(selectedOption);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedPathName]);

  useLayoutEffect(() => {
    if (!effectiveHideDropdown) {
      setBottomSpacer(0);
      return undefined;
    }

    const cfg =
      launchPadImageConfig[resolvedPathName] || launchPadImageConfig.default;
    const imageTopOffset = isHomeStyle ? 90 : 82;
    const imageBottomExtent = cfg.height - imageTopOffset;
    const reservedGap = 150;

    const updateSpacer = () => {
      const headerHeight =
        headerRef.current?.getBoundingClientRect().height || 0;
      setBottomSpacer(
        Math.max(0, imageBottomExtent - headerHeight - reservedGap)
      );
    };

    updateSpacer();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateSpacer);
      return () => window.removeEventListener('resize', updateSpacer);
    }

    const observer = new ResizeObserver(updateSpacer);
    if (headerRef.current) {
      observer.observe(headerRef.current);
    }

    window.addEventListener('resize', updateSpacer);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateSpacer);
    };
  }, [effectiveHideDropdown, resolvedPathName]);

  const handleQuoteChange = (product) => {
    setSelectedProduct(product);
  };

  const handleQuote = () => {
    if (!selectedProduct) return;
    const route = LAUNCH_PAD_PRODUCT_ROUTES[selectedProduct];
    const currentBaseRoute = getBaseProductRoute(pathName);

    if (!route) {
      return;
    }

    if (route.replace(/\/+$/, '') !== currentBaseRoute) {
      navigate(route);
      return;
    }

    const stepBaseRoute =
      LAUNCH_PAD_PRODUCT_STEP_ROUTES[selectedProduct] || route;
    navigate(`${stepBaseRoute.replace(/\/+$/, '')}/step1`);
  };

  const handleCategorySelect = (category) => {
    setActiveIcon(category);
    const firstOption = productOptions[category]?.[0] || '';
    setSelectedProduct(firstOption);
  };

  return (
    <ScrollFadeWrapper
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Box
        component="header"
        role="banner"
        sx={{
          position: 'relative',
          width: '1113px',
          backgroundColor: '#FFFFFF',
          borderRadius: '48px',
          p: '40px',
          overflow: 'visible',
          mt: '40px',
          mr: isHomeStyle ? '120px' : '80px',
          minHeight: effectiveHideDropdown ? 'auto' : '419px',
          zIndex: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: effectiveHideDropdown
            ? 'flex-start'
            : 'space-between',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: isHomeStyle ? '-120px' : '-82px',
            right: isHomeStyle ? '-60px' : '-100px',
            width: '517px',
            height: '517px',
            transformOrigin: 'top right',
            zIndex: 1,
          }}
          aria-hidden="true"
        >
          <ScrollFadeWrapper delay={0.15} startingOpacity={0.5}>
            {determineImage()}
          </ScrollFadeWrapper>
        </Box>

        <Box
          sx={{
            zIndex: 3,
            width: '659px',
            boxSizing: 'border-box',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            flex: '0 0 auto',
          }}
          ref={headerRef}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              component="h1"
              sx={{
                fontFamily: 'Pally, Pally-Fallback, sans-serif',
                fontWeight: 700,
                fontSize: { xs: '2.25rem', md: '4.5rem' },
                lineHeight: { xs: '2.5rem', md: '4.25rem' },
                letterSpacing: '-0.03em',
                whiteSpace: 'pre-line',
                mb: '16px',
                width: '100%',
                maxWidth: '100%',
              }}
            >
              {toSentenceCase(header)}
            </Typography>

            <Typography
              component="h6"
              sx={{
                fontFamily: 'Inter, Inter-Fallback, sans-serif',
                fontWeight: 700,
                fontSize: '1.25rem',
                lineHeight: '1.5rem',
                letterSpacing: '-0.03em',
                mb: effectiveHideDropdown
                  ? '32px'
                  : isHomeStyle
                    ? '32px'
                    : '40px',
                whiteSpace: 'pre-line',
                width: '100%',
              }}
            >
              {toSentenceCase(byLine)}
            </Typography>
          </Box>

          {isHomeStyle && (
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                px: '48.5px',
                boxSizing: 'border-box',
                gap: '32px',
                flexWrap: 'nowrap',
                m: 0,
                mb: '16px',
                justifyContent: 'center',
              }}
            >
              {LAUNCH_PAD_CATEGORY_ORDER.map((label) => {
                const icon = ICONS[label];
                const displayLabel = toTitleCase(
                  CATEGORY_LABELS[label] || label
                );
                const isActive = activeIcon === label;
                return (
                  <Box
                    key={label}
                    onClick={() => handleCategorySelect(label)}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer',
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: isActive ? '#C7CA00' : '#E3E6E8',
                        width: '48px',
                        height: '48px',
                        borderRadius: '20px',
                        padding: '12px',
                        boxSizing: 'border-box',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease-in-out',
                      }}
                    >
                      <Box sx={{ width: 24, height: 24 }}>{icon}</Box>
                    </Box>

                    <Typography
                      component="span"
                      sx={{
                        fontFamily: 'Inter, Inter-Fallback, sans-serif',
                        fontWeight: 600,
                        fontSize: '0.625rem',
                        textTransform: 'none',
                      }}
                    >
                      {displayLabel}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0 }}>
            <QuoteSelectorComponent
              value={selectedProduct}
              onChange={handleQuoteChange}
              onQuote={handleQuote}
              options={productOptions[activeIcon] || []}
              placeholder={`Select ${
                (CATEGORY_LABELS[activeIcon] || activeIcon || 'Hippo') ===
                'Internet'
                  ? 'an'
                  : 'a'
              } ${CATEGORY_LABELS[activeIcon] || activeIcon || 'Hippo'} Product`}
              buttonLabel={button}
              hideDropdown={effectiveHideDropdown}
            />
          </Box>
        </Box>
      </Box>
      {effectiveHideDropdown && (
        <Box
          aria-hidden="true"
          sx={{
            width: '1113px',
            height: `${bottomSpacer}px`,
            flexShrink: 0,
            pointerEvents: 'none',
          }}
        />
      )}
    </ScrollFadeWrapper>
  );
};

export default LaunchPad;
